import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Online workshops are MORE interactive than in-person workshops",
  "description": "",
  "published": "2018-09-05T00:00:00.000Z",
  "redirect_from": ["/blog/online-workshops-more-interactive/will/8603"],
  "categories": "Learning, Livecoding, Personal, Thoughts",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`In August, I ran 3 online workshops about React Data Visualization. Little 4-hour affairs every Sunday for people who preordered my upcoming `}<a parentName="p" {...{
        "href": "https://gumroad.com/l/pMtnZ"
      }}>{`React + D3 2018`}</a>{` book and course.`}</p>
    <p>{`You could call them webinars, but that sounds lame. They're workshops because they're interactive, last a while, and you write code on your own computer. Like an in-person workshop but not.`}</p>
    <p>{`Goal was 👇`}</p>
    <ol>
      <li parentName="ol">{`Give preorderers a nice bonus`}</li>
      <li parentName="ol">{`Practice my Reactathon September workshop`}</li>
      <li parentName="ol">{`Develop and polish some new material`}</li>
      <li parentName="ol">{`Record a new video course`}</li>
    </ol>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/09/CleanShot_2018-08-26-1024x576.png",
        "alt": null
      }}></img></p>
    <p>{`First workshop started with 8 people, and ended with 2. Ouch`}</p>
    <p>{`Second started with 8 people, and ended with 8. Yay`}</p>
    <p>{`Third started with 8 and ended with 11. O.o`}</p>
    <p>{`This can only mean one thing: My workshops improved! The audience was more engaged, grokked the material better, and learned more.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/09/success-kid.jpg",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "how-i-changed-my-approach"
    }}>{`How I changed my approach`}</h2>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`When making workshops, I always change "Here's the solution" 👉 "Here's my solution"`}<br parentName="p"></br><br parentName="p"></br>{`More welcoming. Less judgy. Always okay if you do it different`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1033746917782564864"
      }}>{`August 26, 2018`}</a></blockquote>
    <p>{`My approach to in-person workshops has evolved and improved over the… years?… gosh it's been a while. The main improvements were to make them less ambitious, simplify my code, and do more follow-along exercises.`}</p>
    <p>{`Turns out saying a few words then announcing `}<em parentName="p">{`"Now you code this while I watch"`}</em>{` is not a great teaching approach. People get confused, are afraid to ask questions, and everyone has a bad time. Plus, you get bored.`}</p>
    <p>{`Online, I turn this approach up to eleven.`}</p>
    <p>{`Nothing worse than dead air when you're on air.`}</p>
    <p>{`So where an in-person workshop looks like this:`}</p>
    <ol>
      <li parentName="ol">{`Swiz talks for a while`}</li>
      <li parentName="ol">{`Swiz writes some code`}</li>
      <li parentName="ol">{`Swiz says "Do this next"`}</li>
      <li parentName="ol">{`Everyone except Swiz codes`}</li>
      <li parentName="ol">{`Swiz says "Okay, who got it?"`}</li>
      <li parentName="ol">{`Swiz shows how he'd do it`}</li>
      <li parentName="ol">{`Go to 1`}</li>
    </ol>
    <p>{`An online workshop is `}<em parentName="p">{`more`}</em>{` interactive because I can't see the audience, so I have to make them actively play along with me. Keep them saying stuff in the chatroom, ask them questions, make them guess what I'm going to say next.`}</p>
    <p>{`Something like this 👇`}</p>
    <ol>
      <li parentName="ol">{`Swiz talks for a bit`}</li>
      <li parentName="ol">{`Swiz asks a question to make audience think`}</li>
      <li parentName="ol">{`Swiz waits for chats to fly in`}</li>
      <li parentName="ol">{`Swiz nods and says `}<em parentName="li">{`"YES! Great guess."`}</em></li>
      <li parentName="ol">{`Swiz shows his answer be it code or words or pictures`}</li>
      <li parentName="ol">{`Only at the very end does Swiz say "Okay I'll shush for 5min and you code this"`}</li>
      <li parentName="ol">{`Go to 1`}</li>
    </ol>
    <p>{`I think in a four-hour session there were maybe two 5-minute periods where I was quiet and letting the audience work through some code on their own. Even then, I tried to get them into a live shared Codesandbox so we could work together.`}</p>
    <p>{`Codesandbox live shares are great by the way! You can write some code, then ask the audience to fill in the blank or to guess what has to happen next. It's great 👌`}</p>
    <p>{`And then you get comments like this`}</p>
    <blockquote>
      <p parentName="blockquote">{`How quickly the content made sense. Last week I was having much more trouble than this week, some of the programming challenges were resolved within a minute or 2, which definitely felt rewarding. You did an amazing job putting this together, can't wait to continue reviewing the video!`}</p>
      <p parentName="blockquote">{`Workshop went great, really enjoyed it. You are a great teacher, and give good amounts of time to work things out and answer questions.`}</p>
    </blockquote>
    <p>{`Lesson learned: Online doesn't have to suck.`}</p>
    <p>{`Now I get to try this interactivity`}</p>
    <h2 {...{
      "id": "the-tech"
    }}>{`The tech`}</h2>
    <p>{`Easiest way to do it is hosting a meeting on Zoom. Managing the invites and stuff is annoying and easy to lose track of. There are probably existing perfect tools for that.`}</p>
    <p>{`To record your screen and get a high quality recording, you should use Camtasia. Your computer might struggle running all this stuff. I don't have a solution for that.`}</p>
    <p>{`I used my Canon DSLR as the webcam, so that was high quality.`}</p>
    <p>{`Yeti Blue Mic for good quality audio. Everyone said it was clear and crisp, no background noise.`}</p>
    <p>{`Use a high resolution stream, as high as you can, to avoid having to zoom in too much on your code. The crispness means people can read small fonts.`}</p>
    <p>{`Zoom breakout rooms are great if you wanna help someone one-on-one, a lot like an in-person workshop, but they create dead air for everyone else, which sucks. Better to make the workshop so very interactive that one-on-one help isn't needed.`}</p>
    <h2 {...{
      "id": "more-yes-maybe"
    }}>{`More? Yes, maybe.`}</h2>
    <p>{`Online workshops are a lot more fun than I expected.`}</p>
    <p>{`It's a little nervewracking when you can't see the crowd, don't know if your jokes are landing, aren't sure anyone follows your argument, and don't quite know if your audience is even still there. But you know what? You can compensate for that by making it `}<em parentName="p">{`more`}</em>{` interactive than an in-person workshop `}<em parentName="p">{`and`}</em>{` it's easier to reach people around the world.`}</p>
    <p>{`Think I might host more of these. Interested? `}<a parentName="p" {...{
        "href": "http://twitter.com/swizec"
      }}>{`Ping me on Twitter`}</a>{`. 🤙`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      